/**
 * lbLog - Sistema centralizado de logging com cores e line numbers corretos
 *
 * Usa getters + bind() para preservar o call site real no console do browser.
 * Logs são condicionados ao flag `lb_data.lbdebug` (exceto errors que sempre aparecem).
 * Para ativar debug, defina o cookie `lbdebug=SUCESSO2020`.
 *
 * @example
 * // Helpers por módulo
 * lbLog.form('Config parsed', { target: '#result' });
 * lbLog.media('Upload done', { id: 123 });
 * lbLog.plugins('Loaded Select2');
 *
 * // Com níveis
 * lbLog.form.warn('Campo obrigatório');
 * lbLog.form.error('Falha AJAX', err);  // sempre aparece
 * lbLog.form.success('Enviado');
 *
 * // Agrupamento
 * lbLog.group('Response', () => {
 *   lbLog.form('Status', 'success');
 *   lbLog.form('Data', responseData);
 * });
 *
 * @package Labead
 */

// Verifica se debug está ativo
const isDebug = () => window.lb_data?.lbdebug || false;

// Cores por módulo (para prefixo)
const MODULE_COLORS = {
	LbForm: '#3b82f6',       // blue-500
	MediaManager: '#8b5cf6', // violet-500
	Plugins: '#06b6d4',      // cyan-500
	Select2: '#14b8a6',      // teal-500
	TinyMCE: '#f59e0b',      // amber-500
	Sortable: '#ec4899',     // pink-500
	Calendar: '#10b981',     // emerald-500
	Utils: '#6b7280',        // gray-500
	Lucide: '#a855f7',       // purple-500
	Default: '#64748b',      // slate-500
};

// Noop function para quando debug está desativado
const noop = () => { };

/**
 * Define um logger para um módulo específico usando getters.
 * Getters + bind() preservam o call site correto no console.
 *
 * @param {Object} target - Objeto alvo (lbLog)
 * @param {string} prop - Nome da propriedade (form, media, etc.)
 * @param {string} moduleName - Nome do módulo para display (LbForm, MediaManager, etc.)
 */
function defineModuleLogger(target, prop, moduleName) {
	const color = MODULE_COLORS[moduleName] || MODULE_COLORS.Default;
	const prefix = `%c[${moduleName}]`;
	const style = `color: ${color}; font-weight: bold`;

	Object.defineProperty(target, prop, {
		get() {
			// Quando debug desativado, retorna noop (exceto error)
			if (!isDebug()) {
				const noopLogger = noop;
				noopLogger.log = noop;
				noopLogger.info = noop;
				noopLogger.warn = noop;
				noopLogger.error = console.error.bind(console, prefix, style); // errors sempre aparecem
				noopLogger.success = noop;
				return noopLogger;
			}

			// Debug ativo: retorna console methods bound com prefixo
			// bind() preserva o call site real no browser console
			const logger = console.log.bind(console, prefix, style);
			logger.log = console.log.bind(console, prefix, style);
			logger.info = console.info.bind(console, prefix, style);
			logger.warn = console.warn.bind(console, prefix, style);
			logger.error = console.error.bind(console, prefix, style);
			logger.success = console.log.bind(console, prefix, style);

			return logger;
		},
		configurable: true,
	});
}

// Objeto principal lbLog
const lbLog = {};

// Define loggers por módulo
defineModuleLogger(lbLog, 'form', 'LbForm');
defineModuleLogger(lbLog, 'media', 'MediaManager');
defineModuleLogger(lbLog, 'plugins', 'Plugins');
defineModuleLogger(lbLog, 'select2', 'Select2');
defineModuleLogger(lbLog, 'tinymce', 'TinyMCE');
defineModuleLogger(lbLog, 'sortable', 'Sortable');
defineModuleLogger(lbLog, 'calendar', 'Calendar');
defineModuleLogger(lbLog, 'utils', 'Utils');
defineModuleLogger(lbLog, 'lucide', 'Lucide');

/**
 * Agrupa logs relacionados (não precisa de line number preservation)
 *
 * @param {string} label - Label do grupo
 * @param {Function} fn - Função que executa os logs
 * @param {boolean} collapsed - Se deve iniciar collapsed (default: true)
 */
lbLog.group = function (label, fn, collapsed = true) {
	if (!isDebug()) return;
	if (collapsed) {
		console.groupCollapsed(`%c${label}`, 'color: #94a3b8; font-weight: bold');
	} else {
		console.group(`%c${label}`, 'color: #94a3b8; font-weight: bold');
	}
	try {
		fn();
	} finally {
		console.groupEnd();
	}
};

// Flag de debug (para verificação externa)
lbLog.isDebug = isDebug;

// Exporta como módulo ES6
export default lbLog;

// Também expõe globalmente para uso em scripts externos
if (typeof window !== 'undefined') {
	window.lbLog = lbLog;
}
